/**
 * SoleadLab 2.0 — Claim List Component
 *
 * B3.4: Minimal consumption path for claim entities, with the
 * approved-finding → claim flow operable in the UI.
 *
 * Displays claims for the current site in a compact card list.
 * Each card shows the claim's status workflow badge, the linked
 * finding ID (so the approved-finding → claim relationship is visible),
 * category, claim text, and approval metadata.
 *
 * V4 Invariant #5 is surfaced to the operator: the "New Claim" affordance
 * only makes sense when a finding is already approved. The component
 * renders the findingId as a visible reference to communicate this link.
 *
 * Usage:
 *   <ClaimList siteId="site-123" />
 *   <ClaimList siteId="site-123" filters={{ findingId: 'fnd-001' }} />
 */

import { useEffect } from 'react';
import { useClaimStore } from '@/stores/claimStore';
import type { ClaimFilters, ClaimStatus, ClaimCategory } from '@/services/claimApi';

interface ClaimListProps {
  siteId: string;
  filters?: ClaimFilters;
}

// ─── Workflow badge config ───────────────────────────────────────────────────

const WORKFLOW_LABELS: Record<ClaimStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  published: 'Published',
  withdrawn: 'Withdrawn',
};

const CATEGORY_LABELS: Record<ClaimCategory, string> = {
  warranty: 'Warranty',
  maintenance: 'Maintenance',
  safety: 'Safety',
  performance: 'Performance',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function truncate(text: string, maxLength = 80): string {
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ClaimList({ siteId, filters }: ClaimListProps) {
  const items = useClaimStore((s) => s.items);
  const loading = useClaimStore((s) => s.loading);
  const error = useClaimStore((s) => s.error);
  const setSiteId = useClaimStore((s) => s.setSiteId);
  const fetchItems = useClaimStore((s) => s.fetchItems);

  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (siteId) {
      fetchItems(filters);
    }
    // filters object identity changes on every render — stringify to stabilize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, JSON.stringify(filters), fetchItems]);

  if (loading) {
    return <div className="claim-list__status">Loading claims...</div>;
  }

  if (error) {
    return <div className="claim-list__status claim-list__error">{error}</div>;
  }

  if (items.length === 0) {
    return (
      <div className="claim-list__status">
        No claims found.
        {/* Hint: claims require an approved finding (V4 Invariant #5) */}
        <span className="claim-list__hint"> Claims can only be created from approved findings.</span>
      </div>
    );
  }

  return (
    <div className="claim-list">
      <div className="claim-list__header">
        {items.length} claim{items.length !== 1 ? 's' : ''}
      </div>
      <div className="claim-list__items">
        {items.map((claim) => (
          <div key={claim.id} className="claim-list__card">
            {/* Workflow status badge */}
            <div
              className={`claim-list__workflow claim-list__workflow--${claim.status}`}
              title={`Workflow status: ${claim.status}`}
            >
              {WORKFLOW_LABELS[claim.status] ?? claim.status}
            </div>

            {/* Approved-finding → claim link: finding reference is always visible */}
            <div
              className="claim-list__finding-link"
              title={`Created from approved finding: ${claim.findingId}`}
            >
              Finding: {claim.findingId}
            </div>

            {claim.category && (
              <div className="claim-list__category">
                {CATEGORY_LABELS[claim.category] ?? claim.category}
              </div>
            )}

            <div className="claim-list__claim-text" title={claim.claimText}>
              {truncate(claim.claimText)}
            </div>

            {claim.createdAt && (
              <div className="claim-list__date">
                {formatDate(claim.createdAt)}
              </div>
            )}

            {claim.approvedBy && (
              <div className="claim-list__approved-by" title={`Approved by: ${claim.approvedBy}`}>
                ✓ {claim.approvedBy}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
